import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { useHistory } from "react-router-dom";
import localeText from "../../../../../../locale";
import { formatDate } from "../../../../../../utilities/formatDate";
import { cloudinaryInstance } from "../../../../../../utilities/Cloudinary/cloudinaryInstance";
import "./PendingReceiptReminder.css";

// Abre o diálogo automaticamente apenas uma vez por sessão do navegador
const AUTO_OPEN_KEY = "__extraconomy_pending_receipt_reminder_shown";

const wasAutoOpened = () => {
  try {
    return sessionStorage.getItem(AUTO_OPEN_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const markAutoOpened = () => {
  try {
    sessionStorage.setItem(AUTO_OPEN_KEY, "1");
  } catch (e) {
    // sessionStorage indisponível: o diálogo apenas voltará a abrir no próximo carregamento
  }
};

const daysSince = (date) => {
  const time = new Date(date).getTime();
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((Date.now() - time) / (1000 * 60 * 60 * 24)));
};

const PendingReceiptReminder = ({ orders, loading, receiveOrder }) => {
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [receivingId, setReceivingId] = useState(null);

  // Produtos para download têm fluxo próprio (links), por isso ficam de fora do lembrete
  const pendingOrders = useMemo(
    () =>
      (Array.isArray(orders) ? orders : [])
        .filter((o) => o.status === "SENT" && !o.itens?.[0]?.product?.downloadable)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [orders],
  );

  useEffect(() => {
    if (!loading && pendingOrders.length > 0 && !wasAutoOpened()) {
      markAutoOpened();
      setVisible(true);
    }
  }, [loading, pendingOrders.length]);

  useEffect(() => {
    if (visible && pendingOrders.length === 0 && !receivingId) setVisible(false);
  }, [visible, pendingOrders.length, receivingId]);

  if (pendingOrders.length === 0) return null;

  const count = pendingOrders.length;
  const bannerText = (
    count === 1
      ? localeText("pendingReceiptBannerOne")
      : localeText("pendingReceiptBannerMany")
  ).replace("{count}", count);

  const handleReceive = async (order) => {
    setReceivingId(order.id);
    await receiveOrder(order.buyer.username, order.id, order);
    setReceivingId(null);
    setConfirmingId(null);
  };

  const renderOrder = (order) => {
    const item = order.itens?.[0];
    const imageId = item?.productVariation ? item.productVariation.imageid : item?.product?.imageid1;
    const days = daysSince(order.date);
    const isConfirming = confirmingId === order.id;
    const isReceiving = receivingId === order.id;
    const isService = !!item?.product?.subCategory?.service;

    return (
      <li key={order.id} className="pending-receipt__item">
        <div className="flex align-items-start gap-3">
          {imageId && (
            <AdvancedImage
              cldImg={cloudinaryInstance
                .image(imageId)
                .resize(fill().width(56).height(56))
                .format("webp")}
              className="border-round flex-shrink-0"
              height={56}
              width={56}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap align-items-center gap-2">
              <span className="font-semibold" style={{ color: "var(--theme-text)" }}>
                #{order.id}
              </span>
              {order.itens?.length > 1 && (
                <span className="text-xs" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                  · {order.itens.length} {localeText("pendingReceiptItems")}
                </span>
              )}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("seller")}: {order.seller?.companyName || order.seller?.username}
            </div>
            <div className="text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("pendingReceiptPurchasedOn")} {formatDate(new Date(order.date))}
              {days !== null && (
                <span className={days >= 15 ? "pending-receipt__days--late" : ""}>
                  {" "}
                  ({localeText("pendingReceiptDaysAgo").replace("{days}", days)})
                </span>
              )}
            </div>
            {!isService && order.trackingobject && (
              <div className="text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                {order.shippingcompany ? `${order.shippingcompany} · ` : ""}
                {localeText("trackingObject")}: {order.trackingobject}
              </div>
            )}
          </div>
        </div>

        {isConfirming ? (
          <div className="pending-receipt__confirm mt-3">
            <p className="m-0 text-sm">
              {isService
                ? localeText("pendingReceiptConfirmService")
                : localeText("pendingReceiptConfirmQuestion")}
            </p>
            <div className="flex gap-2 mt-2 justify-content-end">
              <Button
                size="small"
                label={localeText("cancel")}
                className="p-button-text p-button-secondary"
                disabled={isReceiving}
                onClick={() => setConfirmingId(null)}
              />
              <Button
                size="small"
                label={localeText("pendingReceiptConfirmAction")}
                icon="pi pi-check"
                className="p-button-success"
                loading={isReceiving}
                disabled={!!receivingId && !isReceiving}
                onClick={() => handleReceive(order)}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-3 justify-content-end">
            <Button
              size="small"
              label={localeText("orderDetails")}
              icon="pi pi-eye"
              className="p-button-text"
              disabled={!!receivingId}
              onClick={() => history.push(`/dashboard-purchasesdetails?orderId=${order.id}`)}
            />
            <Button
              size="small"
              label={localeText("pendingReceiptIReceived")}
              icon="pi pi-box"
              className="p-button-success p-button-outlined"
              disabled={!!receivingId}
              onClick={() => setConfirmingId(order.id)}
            />
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      <div className="pending-receipt__banner mb-3" role="status">
        <i className="pi pi-truck pending-receipt__banner-icon" />
        <div className="flex-1">
          <div className="font-semibold">{bannerText}</div>
          <div className="text-sm mt-1">{localeText("pendingReceiptBannerHint")}</div>
        </div>
        <Button
          size="small"
          label={localeText("pendingReceiptReviewNow")}
          icon="pi pi-arrow-right"
          iconPos="right"
          className="pending-receipt__banner-button"
          onClick={() => setVisible(true)}
        />
      </div>

      <Dialog
        visible={visible}
        onHide={() => {
          if (!receivingId) {
            setVisible(false);
            setConfirmingId(null);
          }
        }}
        header={localeText("pendingReceiptDialogTitle")}
        style={{ width: "min(40rem, 95vw)" }}
        breakpoints={{ "640px": "95vw" }}
        dismissableMask={!receivingId}
        closable={!receivingId}
        draggable={false}
        footer={
          <Button
            label={localeText("pendingReceiptRemindLater")}
            className="p-button-text"
            disabled={!!receivingId}
            onClick={() => {
              setVisible(false);
              setConfirmingId(null);
            }}
          />
        }
      >
        <p className="mt-0 mb-3" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
          {localeText("pendingReceiptDialogIntro")}
        </p>
        <ul className="list-none m-0 p-0 flex flex-column gap-3">{pendingOrders.map(renderOrder)}</ul>
      </Dialog>
    </>
  );
};

export default PendingReceiptReminder;
